/**
 * theme — light/dark mode + accent colour, applied to :root and persisted.
 *
 * Additive only: this recolours CSS custom properties (both the legacy hex
 * tokens in inherited-styles.css and the shadcn `--tw-*` HSL triplets in
 * index.css) and toggles the `.dark` class on <html>. No existing behaviour,
 * route, chart or table logic is touched — pages keep reading the same vars.
 *
 * Persistence is localStorage (instant, works logged-out). The Settings
 * Appearance card is the UI; account-level sync can layer on later.
 */

export type ThemeMode = 'light' | 'dark';

export interface AccentPreset {
  key: string;
  label: string;
  hex: string;
}

/** The six handoff presets (Settings › Appearance). */
export const ACCENT_PRESETS: AccentPreset[] = [
  { key: 'blue', label: 'Blue', hex: '#2563EB' },
  { key: 'emerald', label: 'Emerald', hex: '#059669' },
  { key: 'violet', label: 'Violet', hex: '#7C3AED' },
  { key: 'sunset', label: 'Sunset', hex: '#EA580C' },
  { key: 'teal', label: 'Teal', hex: '#0D9488' },
  { key: 'indigo', label: 'Indigo', hex: '#4F46E5' },
];

export interface ThemePalette {
  key: string;
  label: string;
  accent: string;
  /** Optional up/down status overrides (e.g. Emerald shifts "up" to teal so
   *  gains stay distinct from a green accent). */
  up?: string;
  down?: string;
  /** Five-step categorical chart ramp, per mode. */
  chartLight: readonly [string, string, string, string, string];
  chartDark: readonly [string, string, string, string, string];
}

/** Full colour schemes (Settings › Appearance). "finance-blue" mirrors the
 *  stylesheet defaults, so it applies no inline overrides. */
export const THEME_PALETTES: ThemePalette[] = [
  {
    key: 'finance-blue', label: 'Finance Blue', accent: '#2563EB',
    chartLight: ['#2563EB', '#0D9488', '#7C3AED', '#EA580C', '#94A3B8'],
    chartDark: ['#5B8DEF', '#2DD4BF', '#A78BFA', '#FB923C', '#7C8798'],
  },
  {
    key: 'emerald', label: 'Emerald Ledger', accent: '#059669', up: '#0D9488',
    chartLight: ['#059669', '#2563EB', '#7C3AED', '#EA580C', '#94A3B8'],
    chartDark: ['#34D399', '#5B8DEF', '#A78BFA', '#FB923C', '#7C8798'],
  },
  {
    key: 'indigo', label: 'Indigo Editorial', accent: '#4F46E5', down: '#E11D48',
    chartLight: ['#4F46E5', '#0D9488', '#D97706', '#DB2777', '#94A3B8'],
    chartDark: ['#818CF8', '#2DD4BF', '#FBBF24', '#F472B6', '#7C8798'],
  },
];

const MODE_KEY = 'vip-theme-mode';
const ACCENT_KEY = 'vip-theme-accent';
const PALETTE_KEY = 'vip-theme-palette';
const DEFAULT_ACCENT = '#2563EB';
const DEFAULT_PALETTE = 'finance-blue';

type Listener = (state: { mode: ThemeMode; accent: string; palette: string }) => void;
const listeners = new Set<Listener>();

let mode: ThemeMode = 'light';
let accent: string = DEFAULT_ACCENT;
let paletteKey: string = DEFAULT_PALETTE;

/* ── colour maths ─────────────────────────────────────────────────────── */

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}

/** #RRGGBB → [h(0-360), s(0-100), l(0-100)]. */
function hexToHsl(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!m) return [221, 83, 53]; // fall back to finance blue
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [Math.round(h), Math.round(s * 100), Math.round(l * 100)];
}

function isValidHex(hex: string): boolean {
  return /^#?[a-f\d]{6}$/i.test(hex.trim());
}

function normalizeHex(hex: string): string {
  const h = hex.trim();
  return h.startsWith('#') ? h : `#${h}`;
}

/* ── apply to :root ───────────────────────────────────────────────────── */

function applyTheme(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // Mode: toggle .dark (index.css + the dark overrides key off this).
  root.classList.toggle('dark', mode === 'dark');

  // Accent: brighten a touch in dark mode for contrast on dark surfaces
  // (mirrors the handoff's brighter #5B8DEF accent).
  const [h, s, lBase] = hexToHsl(accent);
  const l = mode === 'dark' ? clamp(lBase + 12, 0, 92) : lBase;
  const hsl = `${h} ${s}% ${l}%`;
  const solid = `hsl(${h} ${s}% ${l}%)`;
  const tint = `hsl(${h} ${s}% ${l}% / 0.12)`;
  const light = `hsl(${h} ${s}% ${clamp(l + 8, 0, 96)}%)`;

  const set = (k: string, v: string) => root.style.setProperty(k, v);

  // Legacy hex token system (inherited-styles.css).
  set('--primary', solid);
  set('--primary-light', light);
  set('--primary-bg', tint);
  set('--brand', solid);
  set('--brand-bg', tint);
  set('--blue', solid);
  set('--blue-bg', tint);
  set('--cyan', solid);

  // shadcn HSL triplet system (index.css → hsl(var(--tw-*))).
  set('--tw-primary', hsl);
  set('--tw-ring', hsl);

  // Palette scheme: chart ramp + optional up/down status overrides. The
  // default scheme clears the inline overrides so the stylesheet tokens rule.
  const pal = THEME_PALETTES.find((p) => p.key === paletteKey) ?? THEME_PALETTES[0];
  const ramp = mode === 'dark' ? pal.chartDark : pal.chartLight;
  ramp.forEach((c, i) => set(`--chart-${i + 1}`, c));
  const clear = (k: string) => root.style.removeProperty(k);
  if (pal.up) { set('--green', pal.up); set('--green-bg', `${pal.up}1F`); } else { clear('--green'); clear('--green-bg'); }
  if (pal.down) { set('--red', pal.down); set('--red-bg', `${pal.down}1F`); } else { clear('--red'); clear('--red-bg'); }
}

/* ── public API ───────────────────────────────────────────────────────── */

export function initTheme(): void {
  if (typeof window === 'undefined') return;
  const storedMode = window.localStorage.getItem(MODE_KEY);
  const storedAccent = window.localStorage.getItem(ACCENT_KEY);
  if (storedMode === 'dark' || storedMode === 'light') {
    mode = storedMode;
  } else if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    mode = 'dark';
  }
  if (storedAccent && isValidHex(storedAccent)) accent = normalizeHex(storedAccent);
  const storedPalette = window.localStorage.getItem(PALETTE_KEY);
  if (storedPalette && THEME_PALETTES.some((p) => p.key === storedPalette)) {
    paletteKey = storedPalette;
  }
  applyTheme();
}

export function getPalette(): string {
  return paletteKey;
}

/** Select a full colour scheme; also adopts its accent. */
export function setPalette(key: string): void {
  const pal = THEME_PALETTES.find((p) => p.key === key);
  if (!pal) return;
  paletteKey = key;
  accent = pal.accent;
  try {
    window.localStorage.setItem(PALETTE_KEY, key);
    window.localStorage.setItem(ACCENT_KEY, pal.accent);
  } catch { /* ignore */ }
  applyTheme();
  emit();
}

export function getMode(): ThemeMode {
  return mode;
}

export function isDark(): boolean {
  return mode === 'dark';
}

export function getAccent(): string {
  return accent;
}

export function setMode(next: ThemeMode): void {
  mode = next;
  try { window.localStorage.setItem(MODE_KEY, next); } catch { /* ignore */ }
  applyTheme();
  emit();
}

export function toggleMode(): void {
  setMode(mode === 'dark' ? 'light' : 'dark');
}

export function setAccent(hex: string): void {
  if (!isValidHex(hex)) return;
  accent = normalizeHex(hex);
  try { window.localStorage.setItem(ACCENT_KEY, accent); } catch { /* ignore */ }
  applyTheme();
  emit();
}

export function subscribeTheme(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emit(): void {
  const snapshot = { mode, accent, palette: paletteKey };
  listeners.forEach((fn) => fn(snapshot));
}
